import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, RefreshCw, AlertTriangle } from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import { riskExposureService } from '../../services/riskExposureService';

const CreateRisk = () => {
  const navigate = useNavigate();
  const { id: editingRiskId } = useParams();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [isProcessing, setIsProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [riskFormData, setRiskFormData] = useState({
    title: '',
    riskType: 'Credit',
    severity: 'Medium',
    probability: 50,
    impact: 50,
    status: 'Open',
    description: '',
    monitoringFrequency: '',
    lastAssessed: '',
    nextAssessment: '',
  });

  useEffect(() => {
    const loadEditingData = async () => {
      if (editingRiskId) {
        setLoading(true);
        try {
          // Note: If your service doesn't have a getRiskById, you can fetch all and filter or add it.
          // Assuming riskExposureService provides getRisks or similar for now. To be perfectly safe, 
          // we'll try to find it from the list of all risks:
          const response = await riskExposureService.getRisks();
          if (response.success) {
            const risks = response.data?.risks || response.data || [];
            const risk = risks.find(r => r._id === editingRiskId || r.id === editingRiskId);
            if (risk) {
              setRiskFormData({
                title: risk.title || '',
                riskType: risk.riskType || 'Credit',
                severity: risk.severity || 'Medium',
                probability: risk.probability ?? 50,
                impact: risk.impact ?? 50,
                status: risk.status || 'Open',
                description: risk.description || '',
                monitoringFrequency: risk.monitoringFrequency || '',
                lastAssessed: risk.lastAssessed
                  ? new Date(risk.lastAssessed).toISOString().split('T')[0]
                  : '',
                nextAssessment: risk.nextAssessment
                  ? new Date(risk.nextAssessment).toISOString().split('T')[0]
                  : '',
              });
            } else {
              showError('Risk not found');
            }
          }
        } catch (error) {
          showError(error.message || 'Failed to load risk for editing');
        } finally {
          setLoading(false);
        }
      }
    };
    loadEditingData();
  }, [editingRiskId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRiskFormData((prev) => ({ ...prev, [name]: value }));
  };

  const buildRiskPayload = () => ({
    title: riskFormData.title.trim(),
    riskType: riskFormData.riskType,
    severity: riskFormData.severity,
    probability: Number(riskFormData.probability),
    impact: Number(riskFormData.impact),
    status: riskFormData.status,
    description: riskFormData.description.trim(),
    monitoringFrequency: riskFormData.monitoringFrequency,
    lastAssessed: riskFormData.lastAssessed || undefined,
    nextAssessment: riskFormData.nextAssessment || undefined,
  });

  const handleRiskFormSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    try {
      const payload = buildRiskPayload();

      if (editingRiskId) {
        await riskExposureService.updateRisk(editingRiskId, payload);
        success('Risk updated successfully');
      } else {
        await riskExposureService.createRisk(payload);
        success('Risk created successfully');
      }
      
      // Navigate back to risk list
      navigate('/finance/risk');
    } catch (error) {
      showError(error.message || 'Failed to preserve risk record');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    navigate('/finance/risk');
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '14px',
    fontFamily: 'var(--font-family)',
    backgroundColor: 'white',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
    fontFamily: 'var(--font-family)',
  };

  const headerActions = [
    {
      label: 'Cancel',
      icon: <X size={18} />,
      onClick: handleCancel,
      variant: 'outline',
      tooltip: 'Cancel and go back',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw size={24} className="spinning text-gray-400" />
      </div>
    );
  }

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title={editingRiskId ? "Edit Risk" : "New Risk"}
        description="Detail potential financial risks and their corresponding exposures"
        actions={headerActions}
      />

      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        <div style={{ width: '100%', margin: '0 auto' }}>
          <form onSubmit={handleRiskFormSubmit}>
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                marginBottom: '24px',
                overflow: 'hidden',
              }}
            >
              {/* Card Header */}
              <div
                style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <AlertTriangle size={18} style={{ color: 'var(--primary-color)' }} />
                <h3
                  style={{
                    margin: 0,
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#111827',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  Risk Parameters
                </h3>
              </div>

              {/* Card Body */}
              <div style={{ padding: '24px' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '20px',
                  }}
                >
                  {/* Title */}
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={riskFormData.title}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* Risk Type */}
                  <div>
                    <label style={labelStyle}>Risk Type</label>
                    <select
                      name="riskType"
                      value={riskFormData.riskType}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="Credit">Credit</option>
                      <option value="Liquidity">Liquidity</option>
                      <option value="Market">Market</option>
                      <option value="Operational">Operational</option>
                      <option value="Compliance">Compliance</option>
                    </select>
                  </div>
                  
                  {/* Severity */}
                  <div>
                    <label style={labelStyle}>Severity</label>
                    <select
                      name="severity"
                      value={riskFormData.severity}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>

                  {/* Probability */}
                  <div>
                    <label style={labelStyle}>Probability (%)</label>
                    <input
                      type="number"
                      name="probability"
                      min="0"
                      max="100"
                      value={riskFormData.probability}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  {/* Impact */}
                  <div>
                    <label style={labelStyle}>Impact (%)</label>
                    <input
                      type="number"
                      name="impact"
                      min="0"
                      max="100"
                      value={riskFormData.impact}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  {/* Status */}
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Status</label>
                    <select
                      name="status"
                      value={riskFormData.status}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="Open">Open</option>
                      <option value="Mitigated">Mitigated</option>
                      <option value="Accepted">Accepted</option>
                      <option value="Transferred">Transferred</option>
                    </select>
                  </div>

                  {/* Description */}
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Description *</label>
                    <textarea
                      name="description"
                      rows={4}
                      value={riskFormData.description}
                      onChange={handleChange}
                      required
                      style={{
                        ...inputStyle,
                        resize: 'vertical',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions - Now inside the card */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  padding: '20px 24px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'var(--font-family)',
                    color: 'white',
                    backgroundColor: 'var(--primary-color)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {isProcessing ? (
                    <>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {editingRiskId ? 'Save Changes' : 'Save Risk'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreateRisk;
